import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { TagBadge, type Tag } from "@/components/tags/TagBadge"
import { TagColorPicker, TagIconPicker } from "@/components/tags/TagFieldPickers"
import { TAG_COLOR_NAMES } from "@/lib/tag-styles"
import { TAG_ICON_NAMES } from "@/lib/tag-icons"
import type { PageProps } from "@/types/inertia"

export type TagRecord = Tag & { position: number | null }

type Props = {
  open: boolean
  onOpenChange: (next: boolean) => void
  tag: TagRecord | null
}

// Dialog for creating or editing a single tag — name, color, icon — with a
// live badge preview. PATCHes /tags/:id or POSTs /tags.
export function TagForm({ open, onOpenChange, tag }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const [name, setName] = React.useState("")
  const [color, setColor] = React.useState<string>(TAG_COLOR_NAMES[0])
  const [icon, setIcon] = React.useState<string>(TAG_ICON_NAMES[0])
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    setName(tag?.name ?? "")
    setColor(tag?.color ?? TAG_COLOR_NAMES[0])
    setIcon(tag?.icon ?? TAG_ICON_NAMES[0])
  }, [open, tag])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = { tag: { name, color, icon } }
    const onFinish = () => setSubmitting(false)
    const onSuccess = () => onOpenChange(false)
    if (tag) {
      router.patch(`/tags/${tag.id}`, payload, { preserveScroll: true, onFinish, onSuccess })
    } else {
      router.post("/tags", payload, { preserveScroll: true, onFinish, onSuccess })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{tag ? `Edit “${tag.name}”` : "New tag"}</DialogTitle>
          <DialogDescription>
            A tag is a small colored label a piece can wear. A piece can have
            many tags.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="tag-name">Name</label>
            <Input
              id="tag-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Launch"
            />
            {errors.name && <p className="text-sm text-danger-display">{errors.name}</p>}
          </div>

          <div className="flex flex-col gap-2">
            <label>Color</label>
            <TagColorPicker value={color} onChange={setColor} />
          </div>

          <div className="flex flex-col gap-2">
            <label>Icon</label>
            <TagIconPicker value={icon} onChange={setIcon} />
          </div>

          <div className="flex flex-col gap-2">
            <label>Preview</label>
            <div>
              <TagBadge tag={{ id: 0, name: name.trim() || "Tag name", color, icon }} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || name.trim().length === 0}>
              {submitting ? "Saving…" : tag ? "Save changes" : "Create tag"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
